import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Building2, User, Users, Truck, Folder, FolderPlus, Search,
  ChevronRight, Sparkles, ArrowLeft, FileText, Loader2,
  Trash2, FolderTree as FolderTreeIcon, Lock, Archive, RotateCcw,
  Star, Link2, Shield, LayoutGrid, List as ListIcon, Zap, Pin, Upload, MoreHorizontal,
  Plus, Calendar, Briefcase, Tag as TagIcon, Settings, Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import InfoTooltip from '../components/InfoTooltip';
import FolderActions from '../components/FolderActions';
import UpsellBanner from '../components/UpsellBanner';
import BrandEmptyState from '../components/landing/BrandEmptyState';
import SuggestedTemplatesCard from '../components/SuggestedTemplatesCard';
import { ApplyTemplateDialog } from '../components/documents/ApplyTemplateDialog';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import DocumentUploadModal from '../components/DocumentUploadModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPACE_META = {
  firm: {
    name: 'Organization Files', icon: Building2, color: '#3b82f6',
    description: 'Internal documents shared across your organization.',
    info: (
      <>
        <p><strong>Who sees these?</strong> Everyone on your team with workspace access.</p>
        <p><strong>What goes here?</strong> Company-wide files like policies, handbooks, insurance, finance master records, marketing assets and SOPs.</p>
        <p className="text-xs italic">Think of this as your firm's shared drive.</p>
      </>
    ),
  },
  personal: {
    name: 'My Files', icon: User, color: '#8b5cf6',
    description: 'Private to you. No one else on the team can see these.',
    info: (
      <>
        <p><strong>Who sees these?</strong> Only you. Even admins cannot view another user's personal space.</p>
        <p><strong>What goes here?</strong> Personal productivity notes, drafts you're not ready to share, your own CPE certificates, mileage logs and personal tax documents.</p>
        <p className="text-xs italic">Locked to your account — perfect for a personal scratchpad.</p>
      </>
    ),
  },
  client: {
    name: 'Clients', icon: Users, color: '#10b981',
    description: 'Each customer gets their own folder. Synced with the client profile.',
    info: (
      <>
        <p><strong>Who sees these?</strong> Workspace members with access to that client.</p>
        <p><strong>What goes here?</strong> Engagement letters, deliverables, source documents and billing records for each <em>active customer</em>.</p>
        <p className="text-xs italic">A root folder is auto-created whenever you add a new customer contact.</p>
      </>
    ),
  },
  vendor: {
    name: 'Vendors', icon: Truck, color: '#f59e0b',
    description: 'Each vendor gets their own folder. Synced with the vendor profile.',
    info: (
      <>
        <p><strong>Who sees these?</strong> Workspace members with access.</p>
        <p><strong>What goes here?</strong> W-9s, contracts, COIs, recurring invoices, and 1099 history for every <em>supplier or contractor</em> you pay.</p>
        <p className="text-xs italic">A root folder is auto-created whenever you add a new vendor contact.</p>
      </>
    ),
  },
  prospect: {
    name: 'Prospects', icon: Sparkles, color: '#06b6d4',
    description: 'Leads, pitches and pre-customers. Move them to Clients once they sign.',
    info: (
      <>
        <p><strong>Who sees these?</strong> Workspace members with access.</p>
        <p><strong>What goes here?</strong> RFPs, proposals, pitch decks, discovery notes, and any work-in-progress for <em>leads that have not signed yet</em>.</p>
        <p className="text-xs italic">When a prospect converts, copy their folder into <strong>Clients</strong> to keep the pre-sale history intact.</p>
      </>
    ),
  },
};

export default function DocumentsHub() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const wsId = currentWorkspace?.workspace_id;

  const [spaces, setSpaces] = useState([]);
  const [customSpaces, setCustomSpaces] = useState([]);
  // activeSpace == "firm" | "personal" | ... | "custom"
  // When "custom", activeCustomSpaceId points at the specific space row.
  const [activeSpace, setActiveSpace] = useState(() => searchParams.get('space') || 'firm');
  const [activeCustomSpaceId, setActiveCustomSpaceId] = useState(() => searchParams.get('csid') || null);
  const [showNewCustomSpace, setShowNewCustomSpace] = useState(false);
  const [newCustomSpace, setNewCustomSpace] = useState({ name: '', icon: 'Briefcase', color: '#6366f1', scope: 'shared', description: '' });
  const [showDedupe, setShowDedupe] = useState(false);
  const [dedupePlan, setDedupePlan] = useState(null);
  const [dedupeBusy, setDedupeBusy] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetPlan, setResetPlan] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetIncludeContent, setResetIncludeContent] = useState(false);
  const [simplifyDismissed, setSimplifyDismissed] = useState(true);

  useEffect(() => {
    if (!wsId) return;
    setSimplifyDismissed(localStorage.getItem(`cb_simplify_folders_dismissed_${wsId}`) === '1');
  }, [wsId]);

  const dismissSimplify = () => {
    if (wsId) localStorage.setItem(`cb_simplify_folders_dismissed_${wsId}`, '1');
    setSimplifyDismissed(true);
  };
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(() => searchParams.get('folder') || null); // null = space root
  const [breadcrumb, setBreadcrumb] = useState([]); // array of folder objects
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [applying, setApplying] = useState(false);

  // Phase A: view modes (folders | trash | archive) + drag state + undo
  const [viewMode, setViewMode] = useState('folders');
  const [draggedId, setDraggedId] = useState(null);
  const [hoverTargetId, setHoverTargetId] = useState(null);
  const [undoState, setUndoState] = useState(null); // { kind, folderId, prevParent, message }
  const justDraggedRef = useRef(false); // suppress click right after a drag

  // Phase B: Smart Folders + multi-select
  const [smartFolders, setSmartFolders] = useState([]);
  const [activeSmartId, setActiveSmartId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(null); // { op: 'delete' | 'archive' | 'tag', preview: [...] }
  const [showNewSmart, setShowNewSmart] = useState(false);
  const [smartDraft, setSmartDraft] = useState({ name: '', query: '', tags: '', spaces: [] });

  // Phase C: Favorites + View mode + Permissions modal
  const [favorites, setFavorites] = useState([]);
  const [layout, setLayout] = useState(() => localStorage.getItem('docs_layout') || 'list');
  const [permsModal, setPermsModal] = useState(null); // { folder, perms: {user_id: role}, draftUser, draftRole }

  // Replaces window.prompt() — the preview iframe sandbox can block native
  // prompts which is why Rename/Move/Tags appeared broken to users. Single
  // controlled dialog covers all 3 actions with a clean Submit/Cancel UX.
  const [inputModal, setInputModal] = useState(null); // { title, description, label, value, placeholder, confirmLabel, onSubmit }
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const searchRef = useRef(null);

  // Document preview modal: opens when the user clicks a document card or
  // arrives with ?doc=<id> in the URL (e.g., from the Save chat toast).
  const [previewDocId, setPreviewDocId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Auto-open preview from URL once on mount. We strip ?doc after consumption
  // so reloads don't keep re-opening.
  useEffect(() => {
    const docParam = searchParams.get('doc');
    if (docParam) {
      setPreviewDocId(docParam);
      const next = new URLSearchParams(searchParams);
      next.delete('doc');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load spaces summary
  const fetchSpaces = useCallback(async () => {
    if (!wsId) return;
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/spaces`);
      if (res.ok) setSpaces(await res.json());
    } catch {}
  }, [wsId]);

  // Load user's custom spaces
  const fetchCustomSpaces = useCallback(async () => {
    if (!wsId) return;
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/custom-spaces`);
      if (res.ok) setCustomSpaces(await res.json());
    } catch {}
  }, [wsId]);

  useEffect(() => { fetchCustomSpaces(); }, [fetchCustomSpaces]);

  // Load folders for current space + folder (or trash/archive if viewMode says so)
  const fetchFolders = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      let res;
      if (viewMode === 'smart' && activeSmartId) {
        res = await apiFetch(`${API_URL}/api/smart-folders/${activeSmartId}/results`);
        setDocs([]);
        if (res.ok) setFolders(await res.json());
      } else if (viewMode === 'trash') {
        res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folders/trash`);
        setDocs([]);
        if (res.ok) setFolders(await res.json());
      } else if (viewMode === 'archive') {
        res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folders/archive`);
        setDocs([]);
        if (res.ok) setFolders(await res.json());
      } else {
        const params = new URLSearchParams({ space: activeSpace });
        params.append('parent_folder_id', currentFolderId || 'root');
        if (activeSpace === 'custom' && activeCustomSpaceId) {
          params.append('custom_space_id', activeCustomSpaceId);
        }
        res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folders?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          let filtered = data;
          if ((activeSpace === 'client' || activeSpace === 'vendor') && !currentFolderId) {
            filtered = data.filter(f => f.is_contact_root);
          }
          setFolders(filtered);
        }
        // Load docs in current folder
        if (currentFolderId) {
          const dr = await apiFetch(`${API_URL}/api/folders/${currentFolderId}/documents`);
          if (dr.ok) setDocs(await dr.json());
        } else {
          setDocs([]);
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [wsId, activeSpace, activeCustomSpaceId, currentFolderId, viewMode, activeSmartId]);

  // Smart Folders sidebar list
  const fetchSmartFolders = useCallback(async () => {
    if (!wsId) return;
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/smart-folders`);
      if (r.ok) setSmartFolders(await r.json());
    } catch (e) { console.error(e); }
  }, [wsId]);

  useEffect(() => { fetchSmartFolders(); }, [fetchSmartFolders]);

  // Phase C: Favorites
  const fetchFavorites = useCallback(async () => {
    if (!wsId) return;
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/favorites`);
      if (r.ok) setFavorites(await r.json());
    } catch (e) { console.error(e); }
  }, [wsId]);

  useEffect(() => { fetchFavorites(); }, [fetchFavorites]);

  // Phase C: Workspace members directory (used by permissions modal)
  const fetchWorkspaceMembers = useCallback(async () => {
    if (!wsId) return;
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/members`);
      if (r.ok) setWorkspaceMembers(await r.json());
    } catch (e) { console.error(e); }
  }, [wsId]);

  useEffect(() => { fetchWorkspaceMembers(); }, [fetchWorkspaceMembers]);

  // Persist layout choice
  useEffect(() => { localStorage.setItem('docs_layout', layout); }, [layout]);

  // Folder ids currently starred (fast lookup)
  const starredIds = useMemo(() => new Set(favorites.map(f => f.folder_id)), [favorites]);
  const decoratedFolders = useMemo(
    () => folders.map(f => ({ ...f, is_starred: starredIds.has(f.folder_id) })),
    [folders, starredIds],
  );

  // Keyboard shortcuts: Ctrl+K (focus search), Ctrl+Shift+N (new folder), Ctrl+Z (undo)
  useEffect(() => {
    const handler = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.shiftKey && (e.key === 'N' || e.key === 'n')) {
        if (viewMode === 'folders') {
          e.preventDefault();
          setShowNewFolder(true);
        }
      } else if (e.key === 'z' || e.key === 'Z') {
        if (undoState) { e.preventDefault(); handleUndo(); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, undoState]);

  useEffect(() => { fetchSpaces(); }, [fetchSpaces]);
  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  // When changing space, reset to root. `customSpaceId` is set only when
  // s === "custom" — for built-in spaces it stays null.
  const switchSpace = (s, customSpaceId = null) => {
    setActiveSpace(s);
    setActiveCustomSpaceId(s === 'custom' ? customSpaceId : null);
    setCurrentFolderId(null);
    setBreadcrumb([]);
    setSearch('');
    setViewMode('folders');
  };

  const drillInto = (folder) => {
    setCurrentFolderId(folder.folder_id);
    setBreadcrumb([...breadcrumb, folder]);
  };

  const goBackTo = (idx) => {
    if (idx === -1) {
      setCurrentFolderId(null);
      setBreadcrumb([]);
    } else {
      const newCrumb = breadcrumb.slice(0, idx + 1);
      setBreadcrumb(newCrumb);
      setCurrentFolderId(newCrumb[newCrumb.length - 1]?.folder_id || null);
    }
  };

  const filteredFolders = useMemo(() => {
    const base = decoratedFolders;
    if (!search) return base;
    return base.filter(f => (f.name || '').toLowerCase().includes(search.toLowerCase()));
  }, [decoratedFolders, search]);

  const handleNewFolder = async () => {
    if (!newFolderName.trim()) return;
    // Guard: at the root of client / vendor spaces, only contact-root folders
    // (auto-created per contact) are surfaced. Creating a free-form folder
    // there results in a "ghost" folder that exists in DB but is filtered out
    // of the UI. Block it with a helpful message instead.
    if ((activeSpace === 'client' || activeSpace === 'vendor') && !currentFolderId) {
      toast.error(
        activeSpace === 'client'
          ? 'Customer folders are created automatically when you add a new customer. Add a customer in CRM, or step into one to add subfolders here.'
          : 'Vendor folders are created automatically when you add a new vendor. Add a vendor in CRM, or step into one to add subfolders here.',
        { duration: 8000 }
      );
      return;
    }
    try {
      const body = {
        name: newFolderName.trim(),
        parent_folder_id: currentFolderId,
        space: activeSpace,
      };
      if (activeSpace === 'custom' && activeCustomSpaceId) {
        body.custom_space_id = activeCustomSpaceId;
      }
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Folder created');
        setNewFolderName('');
        setShowNewFolder(false);
        fetchFolders();
        fetchSpaces();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to create folder');
      }
    } catch { toast.error('Failed to create folder'); }
  };

  const handleDeleteFolder = async (folder) => {
    if (folder.is_contact_root) {
      toast.error('Contact root folders cannot be deleted from here. Remove the contact instead.');
      return;
    }
    if (!window.confirm(`Move "${folder.name}" to Trash? You can restore it within 30 days.`)) return;
    try {
      const res = await apiFetch(`${API_URL}/api/folders/${folder.folder_id}`, { method: 'DELETE' });
      if (res.ok) {
        setUndoState({ kind: 'delete', folderId: folder.folder_id, message: `"${folder.name}" moved to Trash` });
        toast.success('Folder moved to Trash');
        fetchFolders(); fetchSpaces();
      } else {
        const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Failed to delete');
      }
    } catch { toast.error('Failed to delete'); }
  };

  // Phase B: bulk action handlers + multi-select helpers
  const toggleSelect = (folderId, withRange = false) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set(folders.map(f => f.folder_id)));

  const runBulk = async (operation, extra = {}) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folders/bulk`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder_ids: ids, operation, ...extra }),
      });
      if (r.ok) {
        const data = await r.json();
        const ok = data.results?.filter(x => x.success).length || 0;
        const fail = data.results?.filter(x => !x.success).length || 0;
        toast.success(`${ok} succeeded${fail ? ` · ${fail} failed` : ''}`);
        clearSelection();
        setBulkModal(null);
        fetchFolders(); fetchSpaces();
      } else { toast.error('Bulk operation failed'); }
    } catch { toast.error('Bulk operation failed'); }
  };

  // Phase C2: Visible items pagination cap (avoids rendering 1000+ folder cards at once)
  const [visibleCount, setVisibleCount] = useState(100);
  useEffect(() => { setVisibleCount(100); }, [activeSpace, currentFolderId, viewMode, activeSmartId]);

  // Phase C2: Import a JSON folder-tree exported from another workspace
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-uploading same file later
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folders/import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: wsId,
          payload,
          target_parent_folder_id: currentFolderId || null,
          target_space: activeSpace,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(`Imported ${data.imported} folder${data.imported === 1 ? '' : 's'}`);
        fetchFolders(); fetchSpaces();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Import failed');
      }
    } catch {
      toast.error('Invalid JSON file');
    }
  };

  const savePermission = async () => {
    if (!permsModal?.folder || !permsModal.draftUser.trim()) return;
    const r = await apiFetch(`${API_URL}/api/folders/${permsModal.folder.folder_id}/permissions`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: permsModal.draftUser.trim(), role: permsModal.draftRole }),
    });
    if (r.ok) {
      const data = await r.json();
      setPermsModal({ ...permsModal, perms: data.permissions, draftUser: '' });
      toast.success('Permission saved');
    } else { toast.error('Failed to save permission'); }
  };

  // Grant/update one user's role on the current folder; refreshes modal state in place
  const updatePermissionFor = async (uid, role) => {
    if (!permsModal?.folder || !uid) return;
    const r = await apiFetch(`${API_URL}/api/folders/${permsModal.folder.folder_id}/permissions`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: uid, role }),
    });
    if (r.ok) {
      const data = await r.json();
      setPermsModal(m => ({ ...m, perms: data.permissions }));
    } else { toast.error('Failed to update permission'); }
  };

  const removePermission = async (uid) => {
    const r = await apiFetch(`${API_URL}/api/folders/${permsModal.folder.folder_id}/permissions`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: uid, role: null }),
    });
    if (r.ok) {
      const data = await r.json();
      setPermsModal({ ...permsModal, perms: data.permissions });
    }
  };

  // Bulk grant the modal's currently-selected role to every workspace member at once
  const grantAllMembers = async () => {
    if (!permsModal?.folder) return;
    const role = permsModal.draftRole || 'viewer';
    const targets = workspaceMembers.filter(m => !((m.role || '').toLowerCase() in { owner: 1, admin: 1 }));
    let perms = { ...(permsModal.perms || {}) };
    for (const m of targets) {
      const r = await apiFetch(`${API_URL}/api/folders/${permsModal.folder.folder_id}/permissions`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: m.user_id, role }),
      });
      if (r.ok) {
        const data = await r.json();
        perms = data.permissions;
      }
    }
    setPermsModal({ ...permsModal, perms });
    toast.success(`Granted ${role} to ${targets.length} members`);
  };

  const deselectAllMembers = async () => {
    if (!permsModal?.folder) return;
    const ids = Object.keys(permsModal.perms || {});
    if (!ids.length) { toast.info('No permissions to clear'); return; }
    let perms = { ...permsModal.perms };
    for (const uid of ids) {
      const r = await apiFetch(`${API_URL}/api/folders/${permsModal.folder.folder_id}/permissions`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: uid, role: null }),
      });
      if (r.ok) {
        const data = await r.json();
        perms = data.permissions;
      }
    }
    setPermsModal({ ...permsModal, perms });
    toast.success(`Cleared ${ids.length} permission${ids.length === 1 ? '' : 's'}`);
  };

  const createSmartFolder = async () => {
    if (!smartDraft.name.trim()) { toast.error('Name required'); return; }
    const body = {
      name: smartDraft.name.trim(),
      query: smartDraft.query.trim() || null,
      tags: smartDraft.tags.split(',').map(t => t.trim()).filter(Boolean),
      spaces: smartDraft.spaces,
    };
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/smart-folders`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (r.ok) {
        toast.success('Smart folder created');
        setShowNewSmart(false);
        setSmartDraft({ name: '', query: '', tags: '', spaces: [] });
        fetchSmartFolders();
      } else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Failed to create smart folder'); }
  };

  const deleteSmartFolder = async (sfId) => {
    if (!window.confirm('Delete this smart folder? (Your matching folders are NOT deleted)')) return;
    const r = await apiFetch(`${API_URL}/api/smart-folders/${sfId}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Removed'); fetchSmartFolders(); if (activeSmartId === sfId) { setActiveSmartId(null); setViewMode('folders'); } }
  };

  // Clear selection when view changes
  useEffect(() => { clearSelection(); }, [activeSpace, currentFolderId, viewMode, activeSmartId]);

  // Phase A: unified action handler for the FolderActions menu
  const handleFolderAction = async (action, folder, payload) => {
    const fid = folder.folder_id;
    const refresh = () => { fetchFolders(); fetchSpaces(); };
    try {
      if (action === 'rename') {
        setInputModal({
          title: 'Rename folder',
          description: `Pick a new name for "${folder.name}".`,
          label: 'Folder name',
          value: folder.name,
          placeholder: 'New folder name',
          confirmLabel: 'Rename',
          onSubmit: async (next) => {
            if (!next || next === folder.name) return;
            const r = await apiFetch(`${API_URL}/api/folders/${fid}`, {
              method: 'PATCH', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name: next }),
            });
            if (r.ok) { toast.success('Renamed'); refresh(); }
            else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Rename failed'); }
          },
        });
      } else if (action === 'move') {
        // Build a flat picker of every folder in the current space — far
        // friendlier than asking the user to type a folder_id string.
        const candidates = (folders || []).filter(f => f.folder_id !== fid && f._space === folder._space);
        setInputModal({
          title: `Move "${folder.name}"`,
          description: 'Pick a destination folder or leave empty to move to the space root.',
          label: 'Destination',
          type: 'select',
          options: [{ value: '', label: '— Space root —' }].concat(
            candidates.map(c => ({ value: c.folder_id, label: c.name })),
          ),
          value: folder.parent_folder_id || '',
          confirmLabel: 'Move here',
          onSubmit: async (target) => {
            const r = await apiFetch(`${API_URL}/api/folders/${fid}/move`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ new_parent_folder_id: (target || '').trim() || null }),
            });
            if (r.ok) {
              setUndoState({ kind: 'move', folderId: fid, prevParent: folder.parent_folder_id, message: `"${folder.name}" moved` });
              toast.success('Moved'); refresh();
            } else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Move failed'); }
          },
        });
      } else if (action === 'duplicate') {
        const r = await apiFetch(`${API_URL}/api/folders/${fid}/copy`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
        });
        if (r.ok) { toast.success('Duplicated'); refresh(); }
        else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Duplicate failed'); }
      } else if (action === 'color') {
        const r = await apiFetch(`${API_URL}/api/folders/${fid}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ color: payload }),
        });
        if (r.ok) refresh();
        else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Color failed'); }
      } else if (action === 'tags') {
        const cur = (folder.tags || []).join(', ');
        setInputModal({
          title: 'Edit tags',
          description: 'Comma-separated. Tags help group folders across spaces.',
          label: 'Tags',
          value: cur,
          placeholder: 'e.g., 2026, payroll, q1',
          confirmLabel: 'Save tags',
          onSubmit: async (next) => {
            const tags = (next || '').split(',').map(t => t.trim()).filter(Boolean);
            const r = await apiFetch(`${API_URL}/api/folders/${fid}`, {
              method: 'PATCH', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ tags }),
            });
            if (r.ok) { toast.success('Tags updated'); refresh(); }
            else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Tag update failed'); }
          },
        });
      } else if (action === 'lock' || action === 'unlock') {
        const want = action === 'lock';
        const r = await apiFetch(`${API_URL}/api/folders/${fid}/lock?locked=${want}`, { method: 'POST' });
        if (r.ok) { toast.success(want ? 'Locked' : 'Unlocked'); refresh(); }
        else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Lock failed'); }
      } else if (action === 'archive') {
        const r = await apiFetch(`${API_URL}/api/folders/${fid}/archive`, { method: 'POST' });
        if (r.ok) {
          setUndoState({ kind: 'archive', folderId: fid, message: `"${folder.name}" archived` });
          toast.success('Archived'); refresh();
        } else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Archive failed'); }
      } else if (action === 'delete') {
        await handleDeleteFolder(folder);
      } else if (action === 'restore') {
        const r = await apiFetch(`${API_URL}/api/folders/${fid}/restore`, { method: 'POST' });
        if (r.ok) { toast.success('Restored'); refresh(); }
        else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Restore failed'); }
      } else if (action === 'permanent_delete') {
        if (!window.confirm(`Permanently delete "${folder.name}"? This cannot be undone.`)) return;
        const r = await apiFetch(`${API_URL}/api/folders/${fid}?permanent=true`, { method: 'DELETE' });
        if (r.ok) { toast.success('Permanently deleted'); refresh(); }
        else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Delete failed'); }
      } else if (action === 'save_template') {
        setInputModal({
          title: 'Save as template',
          description: 'Save this folder structure as a reusable template you can apply to any workspace.',
          label: 'Template name',
          value: `${folder.name} template`,
          confirmLabel: 'Save template',
          onSubmit: async (name) => {
            if (!name) return;
            const r = await apiFetch(`${API_URL}/api/folder-templates/from-existing-folders?workspace_id=${wsId}`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name, folder_ids: [fid], industry: 'custom', client_type: 'custom' }),
            });
            if (r.ok) { toast.success('Saved as template'); }
            else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Save failed'); }
          },
        });
      } else if (action === 'rollover') {
        setInputModal({
          title: 'Roll forward to next year',
          description: `Clones "${folder.name}" (including subfolders) into a new folder labeled for the target year.`,
          label: 'Target year',
          value: String(new Date().getFullYear() + 1),
          type: 'number',
          confirmLabel: 'Roll forward',
          onSubmit: async (yr) => {
            const target = parseInt(yr, 10);
            if (!target || target < 1990 || target > 2100) { toast.error('Invalid year'); return; }
            const r = await apiFetch(`${API_URL}/api/folders/${fid}/rollover`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ target_year: target, archive_source: false }),
            });
            if (r.ok) {
              const data = await r.json();
              toast.success(`Rolled forward to ${data.target_year}: "${data.folder?.name}"`);
              refresh();
            } else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Rollover failed'); }
          },
        });
      } else if (action === 'toggle_star') {
        const isStarred = starredIds.has(fid);
        const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/favorites/${fid}`, {
          method: isStarred ? 'DELETE' : 'POST',
        });
        if (r.ok) { toast.success(isStarred ? 'Unstarred' : 'Starred'); fetchFavorites(); }
        else { toast.error('Failed to update star'); }
      } else if (action === 'shortcut') {
        const candidates = (folders || []).filter(f => f.folder_id !== fid && f._space === folder._space);
        setInputModal({
          title: 'Create shortcut',
          description: `Place a shortcut to "${folder.name}" inside another folder so it's accessible from multiple places.`,
          label: 'Place shortcut in',
          type: 'select',
          options: [{ value: '', label: '— Space root —' }].concat(
            candidates.map(c => ({ value: c.folder_id, label: c.name })),
          ),
          value: folder.parent_folder_id || '',
          confirmLabel: 'Create shortcut',
          onSubmit: async (dest) => {
            const r = await apiFetch(`${API_URL}/api/folders/shortcuts`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ source_folder_id: fid, parent_folder_id: (dest || '').trim() || null }),
            });
            if (r.ok) { toast.success('Shortcut created'); refresh(); }
            else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Failed'); }
          },
        });
      } else if (action === 'permissions') {
        const r = await apiFetch(`${API_URL}/api/folders/${fid}/permissions`);
        const perms = r.ok ? (await r.json()).permissions : {};
        setPermsModal({ folder, perms, draftUser: '', draftRole: 'viewer' });
      } else if (action === 'toggle_pin') {
        const next = !folder.is_pinned;
        const r = await apiFetch(`${API_URL}/api/folders/${fid}/pin?pinned=${next}`, { method: 'POST' });
        if (r.ok) {
          toast.success(next ? 'Pinned to contact profile' : 'Unpinned');
          refresh();
        } else { toast.error('Failed to update pin'); }
      } else if (action === 'export') {
        const r = await apiFetch(`${API_URL}/api/folders/${fid}/export`);
        if (r.ok) {
          const data = await r.json();
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${(folder.name || 'folder').replace(/[^a-z0-9-]/gi, '_')}.json`;
          document.body.appendChild(a); a.click(); a.remove();
          URL.revokeObjectURL(url);
          toast.success(`Exported ${data.nodes?.length || 0} folder${data.nodes?.length === 1 ? '' : 's'}`);
        } else { toast.error('Export failed'); }
      } else if (action === 'export_zip') {
        const r = await apiFetch(`${API_URL}/api/folders/${fid}/export?format=zip`);
        if (r.ok) {
          const blob = await r.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${(folder.name || 'folder').replace(/[^a-z0-9-]/gi, '_')}.zip`;
          document.body.appendChild(a); a.click(); a.remove();
          URL.revokeObjectURL(url);
          toast.success('ZIP archive downloaded');
        } else { toast.error('Export failed'); }
      } else if (action === 'public_link') {
        const isOn = !!folder.public_share_token;
        if (isOn) {
          const url = `${window.location.origin}/p/folder/${folder.public_share_token}`;
          const choice = window.confirm(`Public link is ACTIVE:\n${url}\n\nOK = copy link · Cancel = revoke link`);
          if (choice) {
            try { await navigator.clipboard.writeText(url); toast.success('Link copied'); } catch { toast.info(url); }
          } else {
            const r = await apiFetch(`${API_URL}/api/folders/${fid}/public-link?enabled=false`, { method: 'POST' });
            if (r.ok) { toast.success('Public link revoked'); refresh(); }
          }
        } else {
          const r = await apiFetch(`${API_URL}/api/folders/${fid}/public-link?enabled=true`, { method: 'POST' });
          if (r.ok) {
            const data = await r.json();
            const url = `${window.location.origin}/p/folder/${data.public_share_token}`;
            try { await navigator.clipboard.writeText(url); toast.success('Public link created & copied'); } catch { toast.success(url); }
            refresh();
          } else { toast.error('Failed to create public link'); }
        }
      }
    } catch (err) {
      console.error(err);
      toast.error('Action failed');
    }
  };

  // Undo handler — only supports move + delete for now
  const handleUndo = async () => {
    if (!undoState) return;
    try {
      if (undoState.kind === 'delete' || undoState.kind === 'archive') {
        await apiFetch(`${API_URL}/api/folders/${undoState.folderId}/restore`, { method: 'POST' });
      } else if (undoState.kind === 'move') {
        await apiFetch(`${API_URL}/api/folders/${undoState.folderId}/move`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_parent_folder_id: undoState.prevParent || null }),
        });
      }
      toast.success('Undone');
      setUndoState(null);
      fetchFolders(); fetchSpaces();
    } catch { toast.error('Undo failed'); }
  };

  // Auto-dismiss undo banner after 10s
  useEffect(() => {
    if (!undoState) return;
    const t = setTimeout(() => setUndoState(null), 10000);
    return () => clearTimeout(t);
  }, [undoState]);

  // Drag-and-drop helpers
  const handleDrop = async (sourceId, targetId) => {
    if (!sourceId || sourceId === targetId) return;
    const source = folders.find(f => f.folder_id === sourceId);
    try {
      const r = await apiFetch(`${API_URL}/api/folders/${sourceId}/move`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_parent_folder_id: targetId }),
      });
      if (r.ok) {
        setUndoState({ kind: 'move', folderId: sourceId, prevParent: source?.parent_folder_id, message: `Folder moved` });
        toast.success('Moved');
        fetchFolders(); fetchSpaces();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Move failed');
      }
    } catch { toast.error('Move failed'); }
  };

  // Apply template into the CURRENT folder
  const openApplyTemplate = () => {
    setShowApplyTemplate(true);
  };

  // Legacy single-target apply (still used by ?apply= deep links) — kept for
  // backwards-compat but new UI flow goes through ApplyTemplateDialog below.
  const handleApplyTemplate = async () => {
    if (!selectedTemplate) { toast.error('Pick a template'); return; }
    setApplying(true);
    try {
      const res = await apiFetch(`${API_URL}/api/folder-templates/${selectedTemplate}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: wsId, target_parent_folder_id: currentFolderId }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Created ${data.created_count} folder${data.created_count === 1 ? '' : 's'}`);
        setShowApplyTemplate(false);
        setSelectedTemplate('');
        fetchFolders();
        fetchSpaces();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to apply');
      }
    } catch { toast.error('Failed to apply'); }
    finally { setApplying(false); }
  };

  // Resolve the meta block for the current space. Custom spaces don't live
  // in SPACE_META — fall back to the matching customSpaces row so the
  // header still renders.
  const activeCustomSpace = customSpaces.find(s => s.space_id === activeCustomSpaceId);
  const currentSpaceMeta = activeSpace === 'custom' && activeCustomSpace
    ? {
        name: activeCustomSpace.name,
        color: activeCustomSpace.color || '#6366f1',
        icon: Folder,  // generic — we keep it simple
        description: activeCustomSpace.description || 'Custom space',
        info: (
          <>
            <p><strong>Who sees these?</strong> {activeCustomSpace.scope === 'personal' ? 'Only you.' : 'Everyone on your team with workspace access.'}</p>
            <p><strong>What goes here?</strong> Anything you want — a custom top-level space you created for this workspace.</p>
          </>
        ),
      }
    : SPACE_META[activeSpace];

  return (
    <div className="space-y-4" data-testid="documents-hub">
      {/* Contextual upsell — auto-picks the right message */}
      <UpsellBanner mode="auto" />

      {/* Undo banner (Phase A) */}
      {undoState && (
        <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-slate-100 rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 animate-in slide-in-from-bottom-2" data-testid="undo-toast">
          <RotateCcw className="w-4 h-4 text-amber-400" />
          <span className="text-sm">{undoState.message}</span>
          <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={handleUndo} data-testid="undo-btn">
            Undo
          </Button>
          <button onClick={() => setUndoState(null)} className="text-slate-400 hover:text-slate-200 ml-1" aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Phase B: Bulk action confirmation modal (with dry-run preview) */}
      {bulkModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" data-testid="bulk-modal">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start gap-3">
                {bulkModal.op === 'delete' ? <Trash2 className="w-6 h-6 text-destructive" />
                  : <Archive className="w-6 h-6 text-amber-500" />}
                <div className="flex-1">
                  <h3 className="font-semibold text-lg capitalize">{bulkModal.op === 'delete' ? 'Move to Trash' : 'Archive'}</h3>
                  <p className="text-sm text-muted-foreground">
                    This will {bulkModal.op === 'delete' ? 'soft-delete' : 'archive'} <strong>{selected.size}</strong> folder{selected.size === 1 ? '' : 's'} and all their subfolders.
                  </p>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto bg-muted/40 rounded p-2 text-xs space-y-1" data-testid="bulk-preview">
                <div className="font-semibold text-muted-foreground mb-1">Affected folders:</div>
                {Array.from(selected).map(id => {
                  const f = folders.find(x => x.folder_id === id);
                  if (!f) return null;
                  const locked = f.is_locked;
                  const root = f.is_contact_root;
                  return (
                    <div key={id} className={cn('flex items-center gap-2', (locked || root) && 'text-amber-600')}>
                      <Folder className="w-3 h-3" />
                      <span className="flex-1 truncate">{f.name}</span>
                      {f.child_count > 0 && <Badge variant="outline" className="text-[9px] h-4 px-1">+{f.child_count} sub</Badge>}
                      {locked && <Lock className="w-3 h-3" />}
                      {root && <Badge variant="outline" className="text-[9px] h-4 px-1">root</Badge>}
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground italic">
                Folders shown in amber will be skipped (locked or contact-root).
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setBulkModal(null)} data-testid="bulk-cancel">Cancel</Button>
                <Button
                  className={bulkModal.op === 'delete' ? 'bg-destructive hover:bg-destructive/90 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
                  onClick={() => runBulk(bulkModal.op)}
                  data-testid="bulk-confirm"
                >
                  Confirm {bulkModal.op === 'delete' ? 'Delete' : 'Archive'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Phase C: Permissions modal — workspace member list with bulk grant/revoke */}
      {permsModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" data-testid="perms-modal">
          <Card className="w-full max-w-lg mx-4">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-500" />
                <h3 className="font-semibold text-lg">Share &amp; Permissions</h3>
              </div>
              <p className="text-xs text-muted-foreground">
                Folder: <strong>{permsModal.folder?.name}</strong>
              </p>

              {/* Default role + bulk buttons */}
              <div className="flex items-center gap-2 flex-wrap bg-muted/40 rounded p-2">
                <span className="text-[11px] font-medium text-muted-foreground">Default role:</span>
                <Select value={permsModal.draftRole} onValueChange={(v) => setPermsModal(m => ({ ...m, draftRole: v }))}>
                  <SelectTrigger className="w-28 h-8 text-xs" data-testid="perm-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={grantAllMembers} data-testid="perm-select-all">
                  Select all
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={deselectAllMembers} data-testid="perm-deselect-all">
                  Deselect all
                </Button>
              </div>

              {/* Workspace member list with per-user toggle + role picker */}
              <div className="max-h-72 overflow-y-auto bg-card border border-border rounded">
                {workspaceMembers.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground italic p-3">No workspace members found.</p>
                ) : workspaceMembers.map(m => {
                  const isAdmin = ['owner', 'admin'].includes((m.role || '').toLowerCase());
                  const currentRole = permsModal.perms?.[m.user_id] || null;
                  const isGranted = !!currentRole;
                  return (
                    <div key={m.user_id} className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0 hover:bg-muted/30" data-testid={`perm-row-${m.user_id}`}>
                      <input
                        type="checkbox"
                        checked={isAdmin || isGranted}
                        disabled={isAdmin}
                        onChange={(e) => {
                          if (isAdmin) return;
                          if (e.target.checked) {
                            updatePermissionFor(m.user_id, permsModal.draftRole || 'viewer');
                          } else {
                            removePermission(m.user_id);
                          }
                        }}
                        className="h-4 w-4 rounded border-input flex-shrink-0"
                        data-testid={`perm-check-${m.user_id}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium truncate">{m.name || m.email || m.user_id}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{m.email}</p>
                      </div>
                      {isAdmin ? (
                        <Badge variant="secondary" className="text-[10px] capitalize">Admin · always</Badge>
                      ) : isGranted ? (
                        <Select value={currentRole} onValueChange={(v) => updatePermissionFor(m.user_id, v)}>
                          <SelectTrigger className="w-24 h-7 text-[11px]" data-testid={`perm-role-${m.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="editor">Editor</SelectItem>
                            <SelectItem value="viewer">Viewer</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-[10px] text-muted-foreground italic">Inherits</span>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-[10px] text-muted-foreground">
                  {Object.keys(permsModal.perms || {}).length} explicit grant{Object.keys(permsModal.perms || {}).length === 1 ? '' : 's'} · {workspaceMembers.length} member{workspaceMembers.length === 1 ? '' : 's'}
                </p>
                <Button variant="outline" size="sm" onClick={() => setPermsModal(null)} data-testid="perm-close-btn">Done</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Phase B: New Smart Folder modal */}
      {showNewSmart && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" data-testid="new-smart-modal">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                <h3 className="font-semibold text-lg">New Smart Folder</h3>
              </div>
              <Input
                placeholder="Name (e.g. All 2026 Tax)"
                value={smartDraft.name}
                onChange={(e) => setSmartDraft(d => ({ ...d, name: e.target.value }))}
                data-testid="smart-name"
              />
              <Input
                placeholder="Search text (optional)"
                value={smartDraft.query}
                onChange={(e) => setSmartDraft(d => ({ ...d, query: e.target.value }))}
                data-testid="smart-query"
              />
              <Input
                placeholder="Tags (comma-separated, optional)"
                value={smartDraft.tags}
                onChange={(e) => setSmartDraft(d => ({ ...d, tags: e.target.value }))}
                data-testid="smart-tags"
              />
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">Limit to spaces (optional):</p>
                <div className="flex gap-1.5 flex-wrap">
                  {['firm', 'personal', 'client', 'vendor', 'prospect'].map(s => {
                    const on = smartDraft.spaces.includes(s);
                    return (
                      <button key={s}
                        onClick={() => setSmartDraft(d => ({
                          ...d,
                          spaces: on ? d.spaces.filter(x => x !== s) : [...d.spaces, s],
                        }))}
                        className={cn(
                          'px-2 py-0.5 rounded-full text-[11px] border',
                          on ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border'
                        )}
                        data-testid={`smart-space-${s}`}
                      >{SPACE_META[s]?.name || s}</button>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setShowNewSmart(false)}>Cancel</Button>
                <Button onClick={createSmartFolder} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="smart-create-btn">
                  Create
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <FolderTreeIcon className="w-7 h-7 text-primary" /> Documents Hub
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            All your business files in one place. Click <strong>Upload</strong> — AI Buck reads each file and puts it in the right folder for you.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setUploadOpen(true)} size="lg" className="bg-emerald-600 hover:bg-emerald-700 shadow-md" data-testid="hub-upload-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Upload
          </Button>
          <input
            type="file"
            accept="application/json,.json"
            id="folder-import-input"
            className="hidden"
            onChange={handleImportFile}
            data-testid="folder-import-input"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="hub-more-menu">
                <MoreHorizontal className="w-4 h-4 mr-1.5" /> More
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate('/folder-automations')} data-testid="manage-automations-btn">
                <Zap className="w-4 h-4 mr-2" /> Automations
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/folder-templates')} data-testid="manage-templates-btn">
                <FolderTreeIcon className="w-4 h-4 mr-2" /> Folder Templates
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => document.getElementById('folder-import-input')?.click()} data-testid="folder-import-btn">
                <Upload className="w-4 h-4 mr-2" /> Import folder structure (JSON)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
        {/* Space sidebar */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 px-1 pb-1 text-xs uppercase tracking-wide text-muted-foreground/80">
            <span>Spaces</span>
            <InfoTooltip title="What are Spaces?" testId="info-spaces" side="bottom">
              <p>Documents are split into <strong>five spaces</strong>, each with different visibility rules.</p>
              <ul className="list-disc pl-4 space-y-1">
                <li><strong>Organization Files</strong> — shared with the whole team.</li>
                <li><strong>My Files</strong> — private to you only.</li>
                <li><strong>Clients</strong> — one folder per signed customer.</li>
                <li><strong>Vendors</strong> — one folder per supplier you pay.</li>
                <li><strong>Prospects</strong> — leads &amp; pitches before they sign.</li>
              </ul>
            </InfoTooltip>
          </div>
          {['firm', 'personal', 'client', 'vendor', 'prospect'].map(key => {
            const meta = SPACE_META[key];
            const Icon = meta.icon;
            const data = spaces.find(s => s.space === key);
            const isActive = activeSpace === key;
            return (
              <div
                key={key}
                role="button"
                tabIndex={0}
                onClick={() => switchSpace(key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); switchSpace(key); } }}
                className={cn(
                  'w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left cursor-pointer',
                  isActive ? 'border-primary bg-primary/5 shadow-sm' : 'border-border bg-card hover:border-muted-foreground/40'
                )}
                data-testid={`space-${key}-btn`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: `${meta.color}20`, color: meta.color }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-sm">{meta.name}</span>
                    {key === 'personal' && <Lock className="w-3 h-3 text-muted-foreground" />}
                    <span onClick={(e) => e.stopPropagation()} className="ml-auto">
                      <InfoTooltip title={meta.name} testId={`info-${key}`} side="right">
                        {meta.info}
                      </InfoTooltip>
                    </span>
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    {data ? `${data.folder_count} folder${data.folder_count === 1 ? '' : 's'}` : '—'}
                  </p>
                </div>
                {isActive && <ChevronRight className="w-4 h-4 text-primary" />}
              </div>
            );
          })}

          {/* Custom spaces — user-defined top-level spaces */}
          {customSpaces.length > 0 && (
            <div className="pt-2 space-y-2" data-testid="custom-spaces-section">
              <div className="px-1 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground/60">
                Custom Spaces
              </div>
              {customSpaces.map(cs => {
                const isActive = activeSpace === 'custom' && activeCustomSpaceId === cs.space_id;
                return (
                  <div
                    key={cs.space_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => switchSpace('custom', cs.space_id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); switchSpace('custom', cs.space_id); } }}
                    className={cn(
                      'w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left cursor-pointer',
                      isActive ? 'border-primary bg-primary/5 shadow-sm' : 'border-border bg-card hover:border-muted-foreground/40'
                    )}
                    data-testid={`custom-space-${cs.space_id}`}
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${cs.color || '#6366f1'}20`, color: cs.color || '#6366f1' }}
                    >
                      <Folder className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-sm truncate">{cs.name}</span>
                        {cs.scope === 'personal' && <Lock className="w-3 h-3 text-muted-foreground" />}
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        {cs.folder_count || 0} folder{cs.folder_count === 1 ? '' : 's'}
                      </p>
                    </div>
                    {isActive && <ChevronRight className="w-4 h-4 text-primary" />}
                  </div>
                );
              })}
            </div>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowNewCustomSpace(true)}
            className="w-full justify-start mt-2 text-muted-foreground hover:text-foreground"
            data-testid="new-custom-space-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Space
          </Button>

          {/* Phase A: Trash + Archive views */}
          <div className="pt-3 mt-1 border-t border-border/60 space-y-1">
            <div className="px-1 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground/60">
              Maintenance
            </div>
            <div
              onClick={() => setViewMode(viewMode === 'archive' ? 'folders' : 'archive')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setViewMode(viewMode === 'archive' ? 'folders' : 'archive'); }}}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer',
                viewMode === 'archive' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300' : 'hover:bg-muted text-muted-foreground'
              )}
              data-testid="view-archive-btn"
            >
              <Archive className="w-4 h-4" /> Archive
              <span onClick={(e) => e.stopPropagation()}>
                <InfoTooltip title="Archive" testId="info-archive" side="right">
                  <p>Move folders out of your active tree without deleting them.</p>
                  <p>Archived folders stay <em>forever</em> until you restore or delete them. Useful for completed engagements, prior-year clients, or wound-down projects.</p>
                </InfoTooltip>
              </span>
            </div>
            <div
              onClick={() => setViewMode(viewMode === 'trash' ? 'folders' : 'trash')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setViewMode(viewMode === 'trash' ? 'folders' : 'trash'); }}}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer',
                viewMode === 'trash' ? 'bg-red-100 text-red-800 dark:bg-red-950/30 dark:text-red-300' : 'hover:bg-muted text-muted-foreground'
              )}
              data-testid="view-trash-btn"
            >
              <Trash2 className="w-4 h-4" /> Trash
              <span onClick={(e) => e.stopPropagation()}>
                <InfoTooltip title="Trash" testId="info-trash" side="right">
                  <p>Soft-deleted folders live here for <strong>30 days</strong> before permanent removal.</p>
                  <p>Restore a folder from Trash to put it back in its original space, or empty Trash to free up space immediately.</p>
                </InfoTooltip>
              </span>
            </div>
            {viewMode === 'trash' && folders.length > 0 && (
              <Button
                size="sm" variant="ghost"
                className="w-full text-destructive justify-start"
                onClick={async () => {
                  if (!window.confirm(`Permanently empty Trash (${folders.length} folder${folders.length === 1 ? '' : 's'})? This cannot be undone.`)) return;
                  const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folders/trash/empty`, { method: 'POST' });
                  if (r.ok) { toast.success('Trash emptied'); fetchFolders(); fetchSpaces(); }
                }}
                data-testid="empty-trash-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Empty Trash
              </Button>
            )}
            <Button
              size="sm" variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={() => { setShowDedupe(true); setDedupePlan(null); }}
              data-testid="open-dedupe-btn"
            >
              <Wand2 className="w-3.5 h-3.5 mr-1.5" /> Clean up duplicates
            </Button>
            <Button
              size="sm" variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={() => { setShowReset(true); setResetPlan(null); }}
              data-testid="open-reset-org-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset Org Files
            </Button>
          </div>

          {/* Phase B: Smart Folders */}
          <div className="pt-3 mt-1 border-t border-border/60 space-y-1">
            <div className="flex items-center justify-between px-1 pb-1">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground/60">
                Smart Folders
                <InfoTooltip title="Smart Folders" testId="info-smart-folders" side="right">
                  <p><strong>Saved searches that auto-collect folders</strong> across all 5 spaces matching your criteria — like Gmail labels.</p>
                  <p>Create one for "All Tax 2026", "Anything tagged urgent", or "All client folders in Texas." Results update live as folders change.</p>
                </InfoTooltip>
              </div>
              <button
                onClick={() => setShowNewSmart(true)}
                className="text-muted-foreground hover:text-primary p-1"
                title="New smart folder"
                data-testid="new-smart-folder-btn"
              ><FolderPlus className="w-3 h-3" /></button>
            </div>
            {smartFolders.length === 0 ? (
              <p className="px-3 py-2 text-[11px] text-muted-foreground/70">No smart folders yet.</p>
            ) : smartFolders.map(sf => {
              const isActive = viewMode === 'smart' && activeSmartId === sf.smart_folder_id;
              return (
                <div key={sf.smart_folder_id}
                  className={cn(
                    'group flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm cursor-pointer transition-colors',
                    isActive ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/30 dark:text-purple-300' : 'hover:bg-muted text-muted-foreground'
                  )}
                  onClick={() => { setViewMode('smart'); setActiveSmartId(sf.smart_folder_id); }}
                  data-testid={`smart-folder-${sf.smart_folder_id}`}
                >
                  <Sparkles className="w-3.5 h-3.5" style={{ color: sf.color }} />
                  <span className="flex-1 truncate">{sf.name}</span>
                  <Badge variant="outline" className="text-[9px] h-4 px-1">{sf.match_count ?? 0}</Badge>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSmartFolder(sf.smart_folder_id); }}
                    className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive/80"
                    title="Remove"
                    data-testid={`delete-smart-${sf.smart_folder_id}`}
                  ><Trash2 className="w-3 h-3" /></button>
                </div>
              );
            })}
          </div>

          {/* Phase C: Favorites */}
          {favorites.length > 0 && (
            <div className="pt-3 mt-1 border-t border-border/60 space-y-1">
              <div className="px-1 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground/60 flex items-center gap-1">
                Favorites
                <InfoTooltip title="Favorites" testId="info-favorites" side="right">
                  <p>Star any folder for one-click access from this sidebar. Stars are per-user — your favorites are not visible to teammates.</p>
                </InfoTooltip>
              </div>
              {favorites.map(fv => (
                <div key={fv.fav_id || fv.folder_id}
                  className="group flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm cursor-pointer hover:bg-muted text-muted-foreground"
                  onClick={() => { setViewMode('folders'); setActiveSpace(fv.space || 'firm'); setCurrentFolderId(fv.folder_id); setBreadcrumb([{ folder_id: fv.folder_id, name: fv.name }]); }}
                  data-testid={`fav-${fv.folder_id}`}
                >
                  <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  <span className="flex-1 truncate">{fv.name}</span>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/favorites/${fv.folder_id}`, { method: 'DELETE' });
                      if (r.ok) fetchFavorites();
                    }}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                    data-testid={`unfav-${fv.folder_id}`}
                  ><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="space-y-4 min-w-0">
          {/* Breadcrumbs + Actions */}
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => goBackTo(-1)}
                  className="text-sm flex items-center gap-1.5 hover:text-primary transition-colors px-2 py-1 rounded"
                  onDragEnter={(e) => {
                    if (viewMode !== 'folders') return;
                    if (!draggedId) return;
                    e.preventDefault();
                    e.currentTarget.classList.add('bg-primary/10', 'ring-1', 'ring-primary');
                  }}
                  onDragOver={(e) => {
                    if (viewMode !== 'folders') return;
                    if (!draggedId) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                  }}
                  onDragLeave={(e) => e.currentTarget.classList.remove('bg-primary/10', 'ring-1', 'ring-primary')}
                  onDrop={async (e) => {
                    if (viewMode !== 'folders') return;
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-primary/10', 'ring-1', 'ring-primary');
                    const sid = e.dataTransfer.getData('application/x-folder-id')
                             || e.dataTransfer.getData('text/plain');
                    if (sid) await handleDrop(sid, null);
                  }}
                  data-testid="bc-space-root"
                >
                  <currentSpaceMeta.icon className="w-4 h-4" style={{ color: currentSpaceMeta.color }} />
                  <span className="font-medium">{currentSpaceMeta.name}</span>
                </button>
                {breadcrumb.map((b, idx) => (
                  <div key={b.folder_id} className="flex items-center gap-2">
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                    <button onClick={() => goBackTo(idx)} className="text-sm hover:text-primary" data-testid={`bc-${idx}`}>
                      {b.name}
                    </button>
                  </div>
                ))}
                <div className="ml-auto flex items-center gap-2">
                  {viewMode === 'folders' && (
                    <>
                      <div className="inline-flex border rounded-md overflow-hidden" data-testid="layout-toggle">
                        <button
                          onClick={() => setLayout('list')}
                          className={cn('px-2 py-1.5 text-xs', layout === 'list' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/50')}
                          aria-label="List view"
                          data-testid="layout-list"
                        ><ListIcon className="w-3.5 h-3.5" /></button>
                        <button
                          onClick={() => setLayout('grid')}
                          className={cn('px-2 py-1.5 text-xs', layout === 'grid' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/50')}
                          aria-label="Grid view"
                          data-testid="layout-grid"
                        ><LayoutGrid className="w-3.5 h-3.5" /></button>
                      </div>
                      <Button size="sm" variant="outline" onClick={openApplyTemplate} data-testid="apply-template-here-btn">
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Apply Template
                      </Button>
                      <Button size="sm" onClick={() => setShowNewFolder(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="new-folder-btn">
                        <FolderPlus className="w-3.5 h-3.5 mr-1.5" /> New Folder
                      </Button>
                    </>
                  )}
                  {viewMode !== 'folders' && (
                    <Button size="sm" variant="outline" onClick={() => { setViewMode('folders'); setActiveSmartId(null); }} data-testid="exit-view-btn">
                      <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to folders
                    </Button>
                  )}
                </div>
              </div>

              {currentFolderId === null && (
                <p className="text-xs text-muted-foreground mt-2">{currentSpaceMeta.description}</p>
              )}
            </CardContent>
          </Card>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search in ${currentSpaceMeta.name}…  (Ctrl+K)`}
              className="pl-10"
              data-testid="docs-search"
            />
          </div>

          {/* Phase B: Multi-select bulk action toolbar */}
          {selected.size > 0 && (
            <Card className="border-primary/50 bg-primary/5">
              <CardContent className="p-3 flex items-center gap-2 flex-wrap" data-testid="bulk-toolbar">
                <span className="text-sm font-medium">{selected.size} selected</span>
                <Button size="sm" variant="ghost" onClick={selectAll} data-testid="bulk-select-all">Select all</Button>
                <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="bulk-clear">Clear</Button>
                <div className="ml-auto flex items-center gap-2">
                  <Button size="sm" variant="outline"
                    onClick={() => setBulkModal({ op: 'archive' })}
                    data-testid="bulk-archive">
                    <Archive className="w-3.5 h-3.5 mr-1.5" /> Archive
                  </Button>
                  <Button size="sm" variant="outline"
                    onClick={() => {
                      const tagInput = window.prompt('Add tags to all selected (comma-separated):', '');
                      if (!tagInput) return;
                      const tags = tagInput.split(',').map(t => t.trim()).filter(Boolean);
                      if (!tags.length) return;
                      runBulk('tag', { tags });
                    }}
                    data-testid="bulk-tag">
                    <FolderTreeIcon className="w-3.5 h-3.5 mr-1.5" /> Tag
                  </Button>
                  <Button size="sm" variant="outline" className="text-destructive"
                    onClick={() => setBulkModal({ op: 'delete' })}
                    data-testid="bulk-delete">
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Move to Trash
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* New folder inline form */}
          {showNewFolder && (
            <Card>
              <CardContent className="p-3 flex items-center gap-2">
                <FolderPlus className="w-4 h-4 text-muted-foreground" />
                <Input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleNewFolder()}
                  placeholder="Folder name"
                  className="flex-1 h-9"
                  autoFocus
                  data-testid="new-folder-name-input"
                />
                <Button size="sm" onClick={handleNewFolder} data-testid="new-folder-submit">Create</Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}>Cancel</Button>
              </CardContent>
            </Card>
          )}

          {/* Apply template — new multi-target dialog */}
          <ApplyTemplateDialog
            open={showApplyTemplate}
            onOpenChange={(v) => { setShowApplyTemplate(v); if (!v) setSelectedTemplate(''); }}
            wsId={wsId}
            activeSpace={activeSpace}
            currentFolderId={currentFolderId}
            spaceName={currentSpaceMeta.name}
            folders={folders}
            onApplied={() => { fetchFolders(); fetchSpaces(); }}
          />

          {/* Content grid */}
          {loading ? (
            <div className="text-center text-muted-foreground py-12">Loading…</div>
          ) : (
            <>
              {/* Contextual template suggestions — only at the firm-space root,
                  only when user has already started organizing (otherwise
                  BrandEmptyState below handles them). */}
              {/* One-time "Simplify my folders" banner — firm root only,
                  shown when folder clutter detected. Reversible (archives,
                  never deletes). Dismissible per workspace. */}
              {viewMode === 'folders' && activeSpace === 'firm' && !currentFolderId && folders.length > 8 && !simplifyDismissed && (
                <Card className="border-emerald-500/30 bg-emerald-500/5" data-testid="simplify-folders-banner">
                  <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">Want a cleaner Documents Hub?</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        You have {folders.length} top-level folders. Switch to the simple 6-folder setup (Invoices, Receipts &amp; Expenses, Bank Statements, Tax Documents, Contracts &amp; Agreements, Other) — old folders are safely archived, never deleted, and you can restore them anytime.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => { setShowReset(true); setResetPlan(null); }}
                        data-testid="simplify-folders-cta"
                      >
                        Simplify my folders
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground"
                        onClick={dismissSimplify}
                        data-testid="simplify-folders-dismiss"
                      >
                        No thanks
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              {viewMode === 'folders' && activeSpace === 'firm' && !currentFolderId && filteredFolders.length > 0 && (
                <SuggestedTemplatesCard
                  existingFolderNames={folders.map(f => f.name)}
                  onApplied={() => {
                    // Refresh folder list to surface the newly created folders
                    setTimeout(() => window.location.reload(), 600);
                  }}
                />
              )}
              {/* Folders */}
              {filteredFolders.length === 0 && docs.length === 0 ? (
                (viewMode === 'folders' && activeSpace === 'firm' && !currentFolderId) ? (
                  <BrandEmptyState
                    overline="Documents · Get Started"
                    title="Organize everything in one vault."
                    accent="one vault."
                    description="Encrypted Cloudflare R2 storage. AI auto-categorization. Smart folders that update themselves. Drag files in, share by link, never lose a tax return again."
                    icon={FolderPlus}
                    tone="violet"
                    primaryCta={{ label: 'New Folder', onClick: () => setShowNewFolder(true), testId: 'empty-new-folder' }}
                    secondaryCta={{ label: 'Apply Template', onClick: openApplyTemplate, testId: 'empty-apply-template' }}
                    testId="documents-empty-state"
                  />
                ) : (
                  <Card>
                    <CardContent className="text-center py-12">
                      <Folder className="w-12 h-12 mx-auto mb-3 text-muted-foreground/40" />
                      <p className="text-sm text-muted-foreground">
                        {viewMode === 'smart' && (() => { const sf = smartFolders.find(s => s.smart_folder_id === activeSmartId); return sf ? `No folders match "${sf.name}" yet.` : 'No matches.'; })()}
                        {viewMode === 'trash' && 'Trash is empty. Deleted folders will show up here for 30 days.'}
                        {viewMode === 'archive' && 'No archived folders. Archive frees up your active tree without deleting anything.'}
                        {viewMode === 'folders' && activeSpace === 'personal' && !currentFolderId && 'Your private space is empty. Anything you create here is only visible to you.'}
                        {viewMode === 'folders' && (activeSpace === 'client' || activeSpace === 'vendor') && !currentFolderId && 'No contacts yet. Create a client or vendor and a folder will appear here automatically.'}
                        {viewMode === 'folders' && activeSpace === 'prospect' && !currentFolderId && 'No prospect folders yet. Click "New Folder" or "Apply Template" to create one for a lead, RFP or pitch.'}
                        {viewMode === 'folders' && currentFolderId && 'Empty folder. Add subfolders, apply a template, or save documents here from any AI Hub.'}
                      </p>
                    </CardContent>
                  </Card>
                )
              ) : (
                <div className={cn(
                  layout === 'grid' && viewMode === 'folders'
                    ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3'
                    : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'
                )}>
                  {filteredFolders.slice(0, visibleCount).map(f => {
                    const isDropTarget = hoverTargetId === f.folder_id;
                    const isDragging = draggedId === f.folder_id;
                    const isInteractive = viewMode === 'folders' && !f.is_contact_root && !f.is_locked;
                    return (
                      <FolderActions key={f.folder_id} folder={f} onAction={handleFolderAction} hideTrigger>
                        <Card
                          onClick={() => {
                            if (justDraggedRef.current) {
                              justDraggedRef.current = false;
                              return;
                            }
                            if (viewMode === 'folders') drillInto(f);
                          }}
                          draggable={isInteractive}
                          onDragStart={(e) => {
                            if (!isInteractive) return;
                            setDraggedId(f.folder_id);
                            // Use both standard text/plain (cross-browser) and a custom type
                            try { e.dataTransfer.setData('text/plain', f.folder_id); } catch {}
                            try { e.dataTransfer.setData('application/x-folder-id', f.folder_id); } catch {}
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragEnd={() => {
                            setDraggedId(null);
                            setHoverTargetId(null);
                            // Suppress the click that fires after a successful drop on some browsers
                            justDraggedRef.current = true;
                            setTimeout(() => { justDraggedRef.current = false; }, 250);
                          }}
                          onDragEnter={(e) => {
                            if (viewMode !== 'folders') return;
                            if (draggedId === f.folder_id) return; // can't drop onto self
                            e.preventDefault();
                            setHoverTargetId(f.folder_id);
                          }}
                          onDragOver={(e) => {
                            if (viewMode !== 'folders') return;
                            if (draggedId === f.folder_id) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                          }}
                          onDragLeave={(e) => {
                            // Only clear if we truly left the card (not moved to a child)
                            const r = e.relatedTarget;
                            if (r && e.currentTarget.contains(r)) return;
                            setHoverTargetId(prev => prev === f.folder_id ? null : prev);
                          }}
                          onDrop={async (e) => {
                            if (viewMode !== 'folders') return;
                            e.preventDefault();
                            e.stopPropagation();
                            const sid = e.dataTransfer.getData('application/x-folder-id')
                                     || e.dataTransfer.getData('text/plain');
                            setHoverTargetId(null);
                            setDraggedId(null);
                            justDraggedRef.current = true;
                            setTimeout(() => { justDraggedRef.current = false; }, 250);
                            if (sid && sid !== f.folder_id) {
                              await handleDrop(sid, f.folder_id);
                            }
                          }}
                          className={cn(
                            'group transition-all',
                            isInteractive ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
                            isDropTarget && 'ring-2 ring-primary border-primary scale-[1.02] shadow-lg',
                            isDragging && 'opacity-40',
                            !isDropTarget && 'hover:shadow-md hover:border-primary/40',
                            f.is_locked && 'ring-1 ring-amber-300/60',
                          )}
                          data-testid={`folder-card-${f.folder_id}`}
                        >
                          <CardContent className="p-3 flex items-center gap-3">
                            {/* Phase B: selection checkbox (hide on contact-root + trash/archive views) */}
                            {viewMode === 'folders' && !f.is_contact_root && (
                              <input
                                type="checkbox"
                                draggable={false}
                                checked={selected.has(f.folder_id)}
                                onChange={(e) => { e.stopPropagation(); toggleSelect(f.folder_id); }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 rounded border-input flex-shrink-0"
                                data-testid={`select-folder-${f.folder_id}`}
                              />
                            )}
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 relative"
                              style={{ background: `${f.color || '#3b82f6'}20`, color: f.color || '#3b82f6' }}
                            >
                              <Folder className="w-5 h-5" />
                              {f.is_locked && (
                                <Lock className="w-3 h-3 absolute -bottom-1 -right-1 bg-background rounded-full p-0.5 text-amber-600" />
                              )}
                              {f.is_shortcut && (
                                <Link2 className="w-3 h-3 absolute -bottom-1 -left-1 bg-background rounded-full p-0.5 text-purple-600" />
                              )}
                              {f.is_pinned && (
                                <Pin className="w-3 h-3 absolute -top-1 -right-1 bg-background rounded-full p-0.5 text-rose-600" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-medium text-sm truncate">{f.name}</span>
                                {f.is_contact_root && (
                                  <Badge variant="outline" className="text-[9px] capitalize">
                                    {f.linked_contacts?.[0]?.contact_type}
                                  </Badge>
                                )}
                                {(f.tags || []).slice(0, 2).map(t => (
                                  <Badge key={t} variant="secondary" className="text-[9px] py-0 h-4">
                                    {t}
                                  </Badge>
                                ))}
                                {(f.tags || []).length > 2 && (
                                  <Badge variant="outline" className="text-[9px] py-0 h-4">
                                    +{f.tags.length - 2}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-[10px] text-muted-foreground">
                                {viewMode === 'trash'
                                  ? `Deleted ${f.deleted_at ? new Date(f.deleted_at).toLocaleDateString() : ''}`
                                  : viewMode === 'archive'
                                    ? `Archived${f.updated_at ? ' ' + new Date(f.updated_at).toLocaleDateString() : ''}`
                                    : `${f.document_count || 0} doc${f.document_count === 1 ? '' : 's'}${f.child_count ? ` · ${f.child_count} sub` : ''}`}
                              </p>
                            </div>
                            <button
                              draggable={false}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => { e.stopPropagation(); handleFolderAction('toggle_star', f); }}
                              className={cn(
                                'p-1 rounded transition-opacity',
                                f.is_starred ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 hover:bg-muted',
                              )}
                              aria-label={f.is_starred ? 'Unstar' : 'Star'}
                              data-testid={`star-folder-${f.folder_id}`}
                            >
                              <Star className={cn('w-3.5 h-3.5', f.is_starred ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground')} />
                            </button>
                            <FolderActions folder={f} onAction={handleFolderAction}>
                              {null}
                            </FolderActions>
                            {viewMode === 'folders' && (
                              <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            )}
                          </CardContent>
                        </Card>
                      </FolderActions>
                    );
                  })}

                  {/* Phase C2: Pagination when there are 100+ folders */}
                  {filteredFolders.length > visibleCount && (
                    <div className="col-span-full flex justify-center py-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setVisibleCount(c => c + 100)}
                        data-testid="load-more-folders"
                      >
                        Load 100 more ({filteredFolders.length - visibleCount} remaining)
                      </Button>
                    </div>
                  )}

                  {/* Documents inside the open folder.
                    * Card is now clickable → opens the saved transcript in
                    * `<DocumentPreviewModal />` so users can actually read,
                    * download (PDF), or continue chatting from where they
                    * left off. */}
                  {/* Upload-into-this-folder card */}
                  {currentFolderId && (
                    <Card
                      className="border-dashed border-2 hover:border-emerald-500/60 hover:bg-emerald-500/5 transition-all cursor-pointer"
                      onClick={() => setUploadOpen(true)}
                      data-testid="folder-upload-card"
                    >
                      <CardContent className="p-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-emerald-500/10 text-emerald-500">
                          <Upload className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-sm block">Upload files</span>
                          <p className="text-[10px] text-muted-foreground">Add documents to this folder</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {currentFolderId && docs.map(d => (
                    <Card
                      key={d.document_id}
                      className="hover:shadow-md hover:border-emerald-500/40 transition-all cursor-pointer"
                      onClick={() => setPreviewDocId(d.document_id)}
                      data-testid={`document-card-${d.document_id}`}
                    >
                      <CardContent className="p-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400">
                          <FileText className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-sm truncate block">{d.title || 'Untitled'}</span>
                          <p className="text-[10px] text-muted-foreground">
                            {d.hub && <>{d.hub} · </>}
                            {d.message_count ? `${d.message_count} messages · ` : ''}
                            {d.updated_at && new Date(d.updated_at).toLocaleDateString()}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Preview modal — mounted at root so it overlays the entire hub. */}
      <DocumentPreviewModal documentId={previewDocId} onClose={() => setPreviewDocId(null)} />
      <DocumentUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        workspaceId={wsId}
        defaultFolderId={currentFolderId}
        onUploaded={() => { fetchFolders(); }}
      />
      {/* Generic input modal (replaces window.prompt for rename/move/tags/etc.
        * — prompts don't work inside iframe sandboxes). */}
      <FolderActionInputDialog modal={inputModal} onClose={() => setInputModal(null)} />

      {/* Create custom space */}
      <Dialog open={showNewCustomSpace} onOpenChange={setShowNewCustomSpace}>
        <DialogContent data-testid="new-custom-space-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-500" />
              Create a new space
            </DialogTitle>
            <DialogDescription>
              Adds a new top-level entry to the sidebar alongside Organization Files, My Files, etc.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-1">Name</label>
              <Input
                value={newCustomSpace.name}
                onChange={(e) => setNewCustomSpace(s => ({ ...s, name: e.target.value }))}
                placeholder="e.g. Tax Year 2025"
                maxLength={60}
                data-testid="new-custom-space-name"
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Visibility</label>
              <Select
                value={newCustomSpace.scope}
                onValueChange={(v) => setNewCustomSpace(s => ({ ...s, scope: v }))}
              >
                <SelectTrigger data-testid="new-custom-space-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="shared">Shared — everyone on the team can see this</SelectItem>
                  <SelectItem value="personal">Personal — only you</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Accent color</label>
              <div className="flex flex-wrap gap-2">
                {['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#64748b'].map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewCustomSpace(s => ({ ...s, color: c }))}
                    className={cn(
                      'w-8 h-8 rounded-lg border-2 transition',
                      newCustomSpace.color === c ? 'border-foreground scale-110' : 'border-transparent'
                    )}
                    style={{ background: c }}
                    aria-label={`Color ${c}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Description (optional)</label>
              <Input
                value={newCustomSpace.description}
                onChange={(e) => setNewCustomSpace(s => ({ ...s, description: e.target.value }))}
                placeholder="What goes in this space?"
                maxLength={200}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewCustomSpace(false)}>Cancel</Button>
            <Button
              data-testid="new-custom-space-submit"
              disabled={!newCustomSpace.name.trim()}
              onClick={async () => {
                try {
                  const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/custom-spaces`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(newCustomSpace),
                  });
                  const data = await r.json();
                  if (!r.ok) { toast.error(data.detail || 'Failed to create space'); return; }
                  toast.success('Space created');
                  setShowNewCustomSpace(false);
                  setNewCustomSpace({ name: '', icon: 'Briefcase', color: '#6366f1', scope: 'shared', description: '' });
                  await fetchCustomSpaces();
                  switchSpace('custom', data.space_id);
                } catch { toast.error('Failed to create space'); }
              }}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              Create space
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dedupe dialog — clean up duplicate folders from repeated template applies */}
      <Dialog open={showDedupe} onOpenChange={(o) => { setShowDedupe(o); if (!o) setDedupePlan(null); }}>
        <DialogContent data-testid="dedupe-dialog" className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-emerald-500" />
              Clean up duplicate folders
            </DialogTitle>
            <DialogDescription>
              Finds folders with the same name at the same level (often left over from applying a template twice) and merges them. The folder with the most content is kept.
            </DialogDescription>
          </DialogHeader>
          {!dedupePlan ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground mb-4">
                Click "Scan" to preview which folders would be merged. Nothing is deleted yet.
              </p>
              <Button
                data-testid="dedupe-scan-btn"
                onClick={async () => {
                  setDedupeBusy(true);
                  try {
                    const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folders/dedupe`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ dry_run: true }),
                    });
                    const data = await r.json();
                    if (!r.ok) { toast.error(data.detail || 'Scan failed'); return; }
                    setDedupePlan(data);
                  } catch { toast.error('Scan failed'); }
                  finally { setDedupeBusy(false); }
                }}
                disabled={dedupeBusy}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                {dedupeBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Scan for duplicates
              </Button>
            </div>
          ) : dedupePlan.folders_to_delete === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">
              No duplicate folders found. You're all clean.
            </div>
          ) : (
            <>
              <div className="bg-muted/40 rounded-lg p-3 text-sm mb-3">
                Found <strong>{dedupePlan.groups}</strong> duplicate group{dedupePlan.groups === 1 ? '' : 's'} — <strong>{dedupePlan.folders_to_delete}</strong> folder{dedupePlan.folders_to_delete === 1 ? '' : 's'} would be moved to Trash. The folder with the most docs/children in each group is kept.
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {dedupePlan.plan.map((p, i) => (
                  <div key={i} className="border border-border rounded-lg p-2.5 text-xs bg-card">
                    <div className="font-medium text-sm mb-1 flex items-center gap-1.5">
                      <Folder className="w-3.5 h-3.5" />
                      "{p.name}" <span className="text-muted-foreground font-normal">in {p.space}</span>
                    </div>
                    <div className="text-emerald-600 dark:text-emerald-400">
                      ✓ Keep 1 (with {p.keep_doc_count} doc{p.keep_doc_count === 1 ? '' : 's'}, {p.keep_child_count} subfolder{p.keep_child_count === 1 ? '' : 's'})
                    </div>
                    <div className="text-rose-600 dark:text-rose-400">
                      ✗ Delete {p.delete.length} duplicate{p.delete.length === 1 ? '' : 's'}
                      {p.delete_doc_count > 0 && <span className="text-amber-600 dark:text-amber-400"> (warn: {p.delete_doc_count} doc{p.delete_doc_count === 1 ? '' : 's'} on dupes — will be orphaned)</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowDedupe(false); setDedupePlan(null); }}>Close</Button>
            {dedupePlan && dedupePlan.folders_to_delete > 0 && (
              <Button
                data-testid="dedupe-confirm-btn"
                onClick={async () => {
                  setDedupeBusy(true);
                  try {
                    const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folders/dedupe`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ dry_run: false }),
                    });
                    const data = await r.json();
                    if (!r.ok) { toast.error(data.detail || 'Dedupe failed'); return; }
                    toast.success(`Cleaned up ${data.folders_deleted} duplicate folder${data.folders_deleted === 1 ? '' : 's'}`);
                    setShowDedupe(false);
                    setDedupePlan(null);
                    fetchFolders();
                    fetchSpaces();
                  } catch { toast.error('Dedupe failed'); }
                  finally { setDedupeBusy(false); }
                }}
                disabled={dedupeBusy}
                className="bg-rose-500 hover:bg-rose-600 text-white"
              >
                {dedupeBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Move {dedupePlan.folders_to_delete} to Trash
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Organization Files dialog — archives current top-level firm
        * folders and re-seeds the simple 6-folder Essentials set. */}
      <Dialog open={showReset} onOpenChange={(o) => { setShowReset(o); if (!o) setResetPlan(null); }}>
        <DialogContent data-testid="reset-org-dialog" className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-emerald-500" />
              Reset Organization Files
            </DialogTitle>
            <DialogDescription>
              Archives every top-level Organization Files folder and re-seeds the simple 6-folder
              set (Invoices, Receipts &amp; Expenses, Bank Statements, Tax Documents, Contracts &amp; Agreements, Other). Folders with content are protected by default —
              they stay put unless you opt in below. Nothing is permanently deleted; you
              can restore archived folders any time from Maintenance → Archive.
            </DialogDescription>
          </DialogHeader>
          {!resetPlan ? (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground mb-4">
                Click "Scan" to preview which folders would be archived and which would
                be kept. Nothing changes until you confirm.
              </p>
              <Button
                data-testid="reset-org-scan-btn"
                onClick={async () => {
                  setResetBusy(true);
                  try {
                    const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folders/reset-organization-files`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ dry_run: true, include_with_content: resetIncludeContent }),
                    });
                    const data = await r.json();
                    if (!r.ok) { toast.error(data.detail || 'Scan failed'); return; }
                    setResetPlan(data);
                  } catch { toast.error('Scan failed'); }
                  finally { setResetBusy(false); }
                }}
                disabled={resetBusy}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                {resetBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Scan organization files
              </Button>
            </div>
          ) : (
            <>
              <div className="bg-muted/40 rounded-lg p-3 text-sm mb-3 space-y-1">
                <div>You currently have <strong>{resetPlan.current_count}</strong> top-level folder{resetPlan.current_count === 1 ? '' : 's'}.</div>
                <div>
                  <span className="text-amber-600 dark:text-amber-400">{resetPlan.will_archive}</span> would be archived,{' '}
                  <span className="text-emerald-600 dark:text-emerald-400">{resetPlan.protected}</span> protected (contain content),{' '}
                  and <span className="text-emerald-600 dark:text-emerald-400">{resetPlan.essentials_to_seed}</span> Essentials seeded.
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm mb-3 px-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetIncludeContent}
                  onChange={(e) => { setResetIncludeContent(e.target.checked); setResetPlan(null); }}
                  className="w-4 h-4"
                  data-testid="reset-include-content"
                />
                Also archive folders that contain documents (still recoverable from Archive)
              </label>
              <div className="space-y-1 max-h-64 overflow-y-auto pr-1 text-xs">
                {resetPlan.plan.map((p) => (
                  <div key={p.folder_id} className={cn(
                    'flex items-center justify-between border border-border rounded px-3 py-1.5',
                    p.will_archive ? 'bg-amber-500/5' : 'bg-emerald-500/5',
                  )}>
                    <div className="flex items-center gap-2">
                      <Folder className="w-3.5 h-3.5" />
                      <span className="font-medium">{p.name}</span>
                      {(p.tags || []).includes('setup-wizard') && <Badge variant="outline" className="text-[9px]">setup-wizard</Badge>}
                      {(p.tags || []).includes('essentials-default') && <Badge variant="outline" className="text-[9px]">essentials</Badge>}
                    </div>
                    <div className="text-muted-foreground">
                      {p.content_count > 0 && <span>{p.content_count} item{p.content_count === 1 ? '' : 's'} · </span>}
                      {p.will_archive ? (
                        <span className="text-amber-600 dark:text-amber-400">→ Archive</span>
                      ) : (
                        <span className="text-emerald-600 dark:text-emerald-400">Keep (protected)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowReset(false); setResetPlan(null); }}>Cancel</Button>
            {resetPlan && (
              <Button
                data-testid="reset-org-confirm-btn"
                onClick={async () => {
                  setResetBusy(true);
                  try {
                    const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/folders/reset-organization-files`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ dry_run: false, include_with_content: resetIncludeContent }),
                    });
                    const data = await r.json();
                    if (!r.ok) { toast.error(data.detail || 'Reset failed'); return; }
                    toast.success(`Archived ${data.archived_count} · Seeded ${data.essentials_seeded} essentials`);
                    setShowReset(false);
                    setResetPlan(null);
                    dismissSimplify();
                    fetchFolders();
                    fetchSpaces();
                  } catch { toast.error('Reset failed'); }
                  finally { setResetBusy(false); }
                }}
                disabled={resetBusy}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                {resetBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                Reset to Essentials
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * FolderActionInputDialog — single dialog shared by rename/move/tags/rollover/
 * shortcut/save-as-template. `modal` is the `inputModal` state from
 * DocumentsHub; passing `null` keeps it closed. Supports a text input or a
 * <select> dropdown (for picking a destination folder).
 */
function FolderActionInputDialog({ modal, onClose }) {
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Sync the field to the dialog's initial value whenever it opens.
  useEffect(() => {
    if (modal) setValue(modal.value ?? '');
  }, [modal]);

  if (!modal) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await modal.onSubmit?.(value);
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="folder-action-dialog">
        <DialogHeader>
          <DialogTitle data-testid="folder-action-dialog-title">{modal.title}</DialogTitle>
          {modal.description && <DialogDescription>{modal.description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-2 py-2">
          <label className="text-xs font-medium text-muted-foreground">{modal.label}</label>
          {modal.type === 'select' ? (
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              autoFocus
              data-testid="folder-action-dialog-select"
            >
              {(modal.options || []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : (
            <Input
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={modal.placeholder || ''}
              type={modal.type || 'text'}
              onKeyDown={(e) => { if (e.key === 'Enter' && !submitting) handleSubmit(); }}
              data-testid="folder-action-dialog-input"
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting} data-testid="folder-action-dialog-cancel">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="folder-action-dialog-submit">
            {submitting ? 'Working…' : (modal.confirmLabel || 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

